import axios, { AxiosError, AxiosResponse } from 'axios'

import { Env, getEnv } from '@cinch/oxd-utils/env/src'

/**
 * Identity service is used when authenticating a user or registering a new users
 *
 * @link https://identity.oxford.somo-tools.com/swagger/index.html
 */
const user = axios.create({ baseURL: getEnv(Env.IdentityServiceUrl) })

export interface UpdatePasswordParams {
  userId: string
  oldPassword: string
  newPassword: string
}

/**
 * This method is to be used when a user wishes to create a new account.
 */
export const registerNewUser = ({
  lifestyle,
  savedSearch,
  ...registrationData
}: Cinch.RegisterRequest): Promise<AxiosResponse<{}> | AxiosError> => {
  const data: Cinch.RegisterRequest = {
    email: registrationData.email,
    password: registrationData.password,
    firstName: registrationData.firstName.substring(0, 50),
    lastName: registrationData.lastName.substring(0, 50),
    preferenceSms: registrationData.preferenceSms,
    preferenceEmail: registrationData.preferenceEmail,
    preferenceDigital: registrationData.preferenceDigital,
    budget: registrationData.budget,
    partExchange: registrationData.partExchange,
    finance: registrationData.finance,
    latitude: registrationData.latitude,
    longitude: registrationData.longitude,
    location: registrationData.location,
    distance: registrationData.distance,
    favouriteVehicleId: registrationData.favouriteVehicleId,
    redirectUrl: registrationData.redirectUrl,
    ...(lifestyle ? { lifestyle } : {}),
    ...(savedSearch ? { savedSearch } : {}),
  }

  if (registrationData.phoneNumber && registrationData.phoneNumber.length > 0) {
    data.phoneNumber = registrationData.phoneNumber
  }

  return user.request({
    url: '/registrations',
    method: 'POST',
    data,
  })
}

/**
 * In order to confirm an account we need to hit this endpoint with the given
 * code.
 */
export const confirmAccount = (token: string, userId: string): Promise<AxiosResponse<any> | AxiosError> => {
  return user.request({
    url: '/registrations/confirm',
    method: 'POST',
    data: { token, userId },
  })
}

export const forgotPassword = (email: string): Promise<AxiosResponse<any> | AxiosError> => {
  return user.request({
    url: '/password/forgotten',
    method: 'POST',
    data: { email },
  })
}

export const verifyResetToken = (email: string, token: string): Promise<AxiosResponse<any> | AxiosError> => {
  return user.request({
    url: '/password/validate',
    method: 'POST',
    data: { email, token },
  })
}

export const resetPassword = ({
  email,
  token,
  newPassword,
}: {
  email: string
  token: string
  newPassword: string
}): Promise<AxiosResponse<any> | AxiosError> => {
  return user.request({
    url: '/password/reset',
    method: 'PUT',
    data: { email, token, newPassword },
  })
}

export const updatePassword = (updatePasswordData: UpdatePasswordParams): Promise<AxiosResponse<any> | AxiosError> => {
  return user.request({
    url: '/password/change',
    method: 'PUT',
    data: {
      userId: updatePasswordData.userId,
      oldPassword: updatePasswordData.oldPassword,
      newPassword: updatePasswordData.newPassword,
    },
  })
}
